import { mediaSchema } from "@/components/Forms/schema/mediaSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
    createDataSelector,
    createResourceCollectionSelector,
    handleJsonApiError,
} from "jsonapi-zod-query";

const resourceSelector = createResourceCollectionSelector({
    type: "media",
    attributesSchema: mediaSchema,
});

const mediaCollectionSelector = createDataSelector(resourceSelector);

export type MediaCollection = ReturnType<typeof mediaCollectionSelector>;
export type Media = MediaCollection[number];

type Props = {
    caseIdUUID: string;
};

export const useFetchMediasForCase = ({ caseIdUUID }: Props): UseQueryResult<MediaCollection> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["case-media", caseIdUUID],
        queryFn: async () => {
            const fetchURL = apiUrl("/media/for-case");
            fetchURL.searchParams.set("caseIdUUID", caseIdUUID);

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    accept: "application/vnd.api+json",
                },
                body: JSON.stringify({
                    caseIdUUID: caseIdUUID,
                }),
            });

            await handleJsonApiError(response);
            return await response.json();
        },
        select: mediaCollectionSelector,
    });
};
